import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { AddMenuItem } from '../TimelinePlayerActionBar/AddMenu/types';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../timeUtils';
import { endChapterCreation, startChapterCreation, timelineActionsStore } from '../store/timelineActions';
import { canCreateANewChapter } from '../utils';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { showErrorToast } from '@/libs/toast/toast';

const useAddChapterItem = (): AddMenuItem | null => {
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { clipData } = useClipsContext();

  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { createNewChapter } = useTranscriptPage();

  const { isCreatingChapter } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);

  const isDisabled =
    !chapters ||
    isCreatingChapter ||
    !canCreateANewChapter({
      currentTime,
      chapters,
      clipData
    });

  const onAddChapter = useCallback(async (): Promise<void> => {
    if (isCreatingChapter) {
      return;
    }
    startChapterCreation();
    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);

    if (!currentSegment) {
      showErrorToast('No segment found at the current time. Please try again.');
      endChapterCreation();
      return;
    }
    const chapterTime = isSplitsEnabled
      ? currentTime
      : getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);

    await createNewChapter('New Chapter', chapterTime);

    endChapterCreation();
  }, [clipData, createNewChapter, currentTime, isCreatingChapter, isSplitsEnabled]);

  return useMemo(() => {
    if (!chapters) {
      return null;
    }

    return {
      id: 'add-chapter',
      label: isCreatingChapter ? 'Adding Chapter...' : 'Add Chapter',
      iconName: 'IconList',
      onClick: onAddChapter,
      isDisabled
    };
  }, [chapters, isCreatingChapter, onAddChapter, isDisabled]);
};

export default useAddChapterItem;
